import { useEffect, useId, useState } from 'react';
import { useLocale } from '../../../i18n';
import {
  readVenueAvailability,
  type VenueAvailability as Availability,
  type VenueCourt,
} from '../../../lib/api/venues';
import { BigButton, Card, EmptyState, SectionTitle } from '../mobileUi';

// ─────────────────────────────────────────────────────────────────────
// WHAT IS FREE AT A VENUE (#833 slice 2).
//
// Explore → Centre → AVAILABLE COURTS → Booking. This is the third step, for
// anybody signed in: the owner's 2026-10-16 decision makes membership a price,
// never access, so a player with no club is shown the same free times a
// member is.
//
// READ-ONLY, ON PURPOSE. The times are the member booking's own — the server
// runs the slot engine `POST /member/bookings` checks against — but nothing
// here books, and nothing names a price: what a non-member pays is not
// decided, and a member's price shown here would read as theirs. The way on
// stays where `MobileVenue` puts it.
//
// DATES ARE THE SERVER'S. It answers which days it will speak about, in the
// venue's own time, so this screen never computes a `YYYY-MM-DD` — it only
// formats one for display, from its parts (never through UTC).
// ─────────────────────────────────────────────────────────────────────

export interface VenueAvailabilityProps {
  slug: string;
  /** The profile's courts, to name a court number the way the venue does. */
  courts: VenueCourt[];
  /** Injected in tests; the real one is `GET /venues/:slug/availability`. */
  load?: typeof readVenueAvailability;
}

type State =
  { state: 'loading' } | { state: 'ok'; availability: Availability | null } | { state: 'failed' };

/** `YYYY-MM-DD` as a LOCAL date, for display only. */
const localDay = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
};

export function VenueAvailability({
  slug,
  courts,
  load = readVenueAvailability,
}: VenueAvailabilityProps) {
  const { t, formatDate } = useLocale();
  const daysLabel = useId();
  // null asks for the venue's today; the server says which day that is.
  const [date, setDate] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<State>({ state: 'loading' });
  // The strip stays while the next day loads, so it does not jump.
  const [days, setDays] = useState<string[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    setResult({ state: 'loading' });
    void load(slug, date ?? undefined, controller.signal).then((outcome) => {
      if (outcome.status === 'aborted') return;
      if (outcome.status === 'ok') {
        setResult({ state: 'ok', availability: outcome.availability });
        if (outcome.availability) setDays(outcome.availability.dates);
      } else setResult({ state: 'failed' });
    });
    return () => controller.abort();
  }, [slug, date, load, attempt]);

  const shown = result.state === 'ok' ? result.availability : null;
  // A venue the server says has no calendar has nothing to show here at all.
  if (result.state === 'ok' && shown === null) return null;

  const selected = shown?.date ?? date ?? days[0] ?? null;
  const courtName = (n: number) =>
    courts.find((c) => c.number === n)?.name ?? t('mobile.venue.courtNumber', { number: n });

  return (
    <div>
      <SectionTitle>{t('mobile.venue.freeTimes')}</SectionTitle>

      {days.length > 0 && (
        <div
          role="group"
          aria-labelledby={daysLabel}
          className="flex gap-1 overflow-x-auto -mx-4 px-4 pb-2 scrollbar-thin"
        >
          <span id={daysLabel} className="sr-only">
            {t('mobile.venue.chooseDay')}
          </span>
          {days.map((d, i) => {
            const day = localDay(d);
            const active = d === selected;
            const weekday =
              i === 0
                ? t('mobile.venue.today')
                : formatDate(day, { weekday: 'short' }).replace('.', '');
            return (
              <button
                key={d}
                type="button"
                onClick={() => setDate(d)}
                aria-pressed={active}
                aria-label={formatDate(day, { weekday: 'long', day: 'numeric', month: 'long' })}
                className="flex-shrink-0 flex flex-col items-center rounded-xl px-2 py-1.5"
                style={{
                  minWidth: 48,
                  background: active ? '#0f1611' : '#fff',
                  color: active ? '#fff' : '#0f1611',
                  border: `1px solid ${active ? '#0f1611' : '#e7eae3'}`,
                }}
              >
                <span className="text-[11px] font-semibold">{weekday}</span>
                <span className="text-[15px] font-semibold tabular-nums">{day.getDate()}</span>
              </button>
            );
          })}
        </div>
      )}

      {result.state === 'loading' && <EmptyState title={t('mobile.venue.loading')} />}
      {result.state === 'failed' && (
        <div className="space-y-2">
          <EmptyState title={t('mobile.venue.availabilityFailed')} />
          <BigButton variant="ghost" onClick={() => setAttempt((n) => n + 1)}>
            {t('mobile.venue.retry')}
          </BigButton>
        </div>
      )}
      {shown && shown.slots.length === 0 && <EmptyState title={t('mobile.venue.noFreeTimes')} />}
      {shown && shown.slots.length > 0 && (
        <>
          <p className="text-[12px] text-ink-2 px-1 mb-1.5">
            {t('mobile.venue.freeTimesFor', { minutes: shown.durationMin })}
          </p>
          <ul className="space-y-1.5">
            {shown.slots.map((s) => (
              <li key={s.startTime}>
                <Card>
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono font-bold text-[14px] text-ink tabular-nums flex-shrink-0">
                      {s.startTime}–{s.endTime}
                    </span>
                    <span className="text-[13px] text-ink-2 min-w-0">
                      {t('mobile.venue.freeCourts', {
                        courts: s.courts.map(courtName).join(', '),
                      })}
                    </span>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
